import {CheckCircleOutlined} from '@ant-design/icons';
import {Button, DatePicker, Form, Input, Modal, Radio, Select, Tag} from 'antd';
import Text from 'antd/es/typography/Text';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import {useState} from 'react';

const {Option} = Select;

const STATUS_CONFIG = {
  PRESENT: {
    label: 'Present',
    color: 'green',
    icon: <CheckCircleOutlined />,
  },
  ABSENT: {
    label: 'Absent',
    color: 'red',
    icon: null,
  },
  HALF_DAY: {
    label: 'Half Day ',
    color: 'orange',
    icon: null,
  },
};

const OVERTIME_OPTIONS = [
  {value: 'NONE',        label: 'None'},
  {value: 'FIRST_HALF',  label: 'First Half'},
  {value: 'SECOND_HALF', label: 'Second Half'},
  {value: 'FULL_DAY',   label: 'Full Day'},
];

const MarkAttendance = ({open, onClose, employee, employeeList = [], onSubmit, loading, alreadyMarked}) => {
  const [form] = Form.useForm();
  const [status, setStatus] = useState('PRESENT');

  const handleClose = () => {
    form.resetFields();
    setStatus('PRESENT');
    onClose();
  };

  const handleFinish = values => {
    const selectedEmployee = employee ?? employeeList.find(e => e.id === values.employeeId);
    if (!selectedEmployee) {return;}

    onSubmit({
      employeeId:   selectedEmployee.id,
      employeeName: `${selectedEmployee.firstName} ${selectedEmployee.lastName}`,
      status:       values.status,
      overtimeType: values.overtimeType ?? 'NONE',
      reason:       values.status === 'ABSENT' ? (values.reason ?? '') : '',
      date:         values.date ? values.date.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
    });
  };

  const isStandalone = !employee;

  return (
    <Modal
      title={(
        <div className={'flex items-center gap-2'}>
          <CheckCircleOutlined className={'text-primary-500'} />
          <span>Mark Attendance</span>
        </div>
      )}
      open={open}
      onCancel={handleClose}
      footer={null}
      width={480}
      destroyOnHidden
    >
      <div className={'mt-4'}>
        {/* Employee info card — only when pre-selected from table */}
        {employee && (
          <div className={'flex items-center gap-3 p-3 bg-gray-50 rounded-lg mb-5'}>
            <div className={'w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center'}>
              <span className={'text-primary-600 font-semibold text-sm'}>
                {employee.firstName?.[0]}{employee.lastName?.[0]}
              </span>
            </div>
            <div>
              <Text className={'font-semibold text-gray-800 block'}>
                {employee.firstName} {employee.lastName}
              </Text>
              <Text className={'text-xs text-gray-400'}>{employee.email}</Text>
            </div>
          </div>
        )}

        {alreadyMarked ? (
          <div className={'text-center py-6'}>
            <CheckCircleOutlined className={'text-green-500 text-3xl mb-2'} />
            <Text className={'block text-gray-600'}>Attendance already marked for today.</Text>
            <Button className={'mt-4'} onClick={handleClose}>Close</Button>
          </div>
        ) : (
          <Form
            form={form}
            layout={'vertical'}
            initialValues={{status: 'PRESENT', overtimeType: 'NONE', date: dayjs()}}
            onFinish={handleFinish}
            onValuesChange={changed => {
              if (changed.status) {setStatus(changed.status);}
            }}
          >
            {/* Employee selector — only in standalone mode */}
            {isStandalone && (
              <Form.Item
                name={'employeeId'}
                label={'Employee'}
                rules={[{required: true, message: 'Please select an employee'}]}
              >
                <Select
                  showSearch
                  placeholder={'Search employee…'}
                  optionFilterProp={'label'}
                  options={employeeList.map(e => ({
                    value: e.id,
                    label: `${e.firstName} ${e.lastName}`,
                  }))}
                />
              </Form.Item>
            )}

            {/* Date picker — always shown */}
            <Form.Item
              name={'date'}
              label={'Attendance Date'}
              rules={[{required: true, message: 'Please select a date'}]}
            >
              <DatePicker
                className={'w-full'}
                format={'DD MMM YYYY'}
                disabledDate={current => current && current > dayjs().endOf('day')}
              />
            </Form.Item>

            <Form.Item
              name={'status'}
              label={'Attendance Status'}
              rules={[{required: true, message: 'Please select a status'}]}
            >
              <Radio.Group className={'flex flex-wrap gap-2'}>
                {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                  <Radio.Button
                    key={key}
                    value={key}
                    className={`rounded-lg ${status === key ? '!border-blue-600 shadow-md shadow-blue-100' : ''}`}
                  >
                    <Tag color={cfg.color} icon={cfg.icon} className={'mr-0 border-0 bg-transparent'}>
                      {cfg.label}
                    </Tag>
                  </Radio.Button>
                ))}
              </Radio.Group>
            </Form.Item>

            <Form.Item
              name={'overtimeType'}
              label={'Overtime'}
            >
              <Select placeholder={'Select overtime type'}>
                {OVERTIME_OPTIONS.map(opt => (
                  <Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            {status === 'ABSENT' && (
              <Form.Item
                name={'reason'}
                label={'Reason for Absence'}
                rules={[{required: true, message: 'Please enter a reason for absence'}]}
              >
                <Input.TextArea rows={3} placeholder={'Enter reason for absence…'} />
              </Form.Item>
            )}

            <div className={'flex justify-end gap-2 mt-6'}>
              <Button onClick={handleClose}>Cancel</Button>
              <Button className={'btn-primary'} htmlType={'submit'} loading={loading}>
                Mark Attendance
              </Button>
            </div>
          </Form>
        )}
      </div>
    </Modal>
  );
};

export default MarkAttendance;

MarkAttendance.propTypes = {
  open:          PropTypes.bool.isRequired,
  onClose:       PropTypes.func.isRequired,
  onSubmit:      PropTypes.func.isRequired,
  loading:       PropTypes.bool,
  alreadyMarked: PropTypes.bool,
  employee:      PropTypes.shape({
    id:        PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    firstName: PropTypes.string,
    lastName:  PropTypes.string,
    email:     PropTypes.string,
  }),
  employeeList: PropTypes.arrayOf(PropTypes.shape({
    id:        PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    firstName: PropTypes.string,
    lastName:  PropTypes.string,
  })),
};
